import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram } from "lucide-react";
import { whatsappHref } from "@/lib/format";

export default function Contacto() {
  return (
    <PageShell testid="page-contacto">
      <SEO
        title="Contacto | Líneas Doradas Transporte Turístico"
        description="Comunícate con Líneas Doradas S.A.S por WhatsApp o correo. Transporte turístico en Bogotá y toda Colombia."
      />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Contacto", url: "https://www.transporteenvanesybuses.com/contacto" },
        ])}
      />

      <section className="relative pt-16 md:pt-24 pb-8">
        <div className="orb-gold w-[500px] h-[500px] right-[-140px] top-[100px] opacity-70" />
        <div className="max-w-4xl mx-auto px-5 md:px-8 relative text-center">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Contacto</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0]">
            <span className="italic text-gold-grad">Contáctanos</span>
          </h1>
          <p className="text-[#F5EFE0]/70 mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Estamos disponibles todos los días. Escríbenos por WhatsApp para respuesta inmediata o al correo para cotizaciones corporativas.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-5">
          <a
            data-testid="contact-whatsapp"
            href={whatsappHref("573123616674", "Hola Líneas Doradas!")}
            target="_blank"
            rel="noreferrer"
            className="card-lux p-8 text-center hover:border-[#D4AF37]/60"
            style={{ transitionProperty: "border-color", transitionDuration: "220ms" }}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center mx-auto mb-5">
              <MessageCircle size={26} className="text-[#E8C87A]" />
            </div>
            <div className="text-[#E8C87A] text-[10px] uppercase tracking-[0.35em] mb-2">WhatsApp</div>
            <div className="font-display text-2xl text-[#F5EFE0] mb-1">+57 312 361 6674</div>
            <div className="text-xs text-[#F5EFE0]/50">Respuesta en 30 min · L–D · 6 a.m. – 10 p.m.</div>
          </a>

          <a
            data-testid="contact-email"
            href="mailto:transporteenvanesybuses@gmail.com"
            className="card-lux p-8 text-center hover:border-[#D4AF37]/60"
            style={{ transitionProperty: "border-color", transitionDuration: "220ms" }}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center mx-auto mb-5">
              <Mail size={26} className="text-[#E8C87A]" />
            </div>
            <div className="text-[#E8C87A] text-[10px] uppercase tracking-[0.35em] mb-2">Correo</div>
            <div className="font-display text-xl text-[#F5EFE0] mb-1 break-all">transporteenvanesybuses@gmail.com</div>
            <div className="text-xs text-[#F5EFE0]/50">Cotizaciones corporativas y contratos</div>
          </a>

          <a
            data-testid="contact-instagram"
            href="https://www.instagram.com/doradaslineas/"
            target="_blank"
            rel="noreferrer"
            className="card-lux p-8 text-center hover:border-[#D4AF37]/60"
            style={{ transitionProperty: "border-color", transitionDuration: "220ms" }}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center mx-auto mb-5">
              <Instagram size={26} className="text-[#E8C87A]" />
            </div>
            <div className="text-[#E8C87A] text-[10px] uppercase tracking-[0.35em] mb-2">Instagram</div>
            <div className="font-display text-2xl text-[#F5EFE0] mb-1">@doradaslineas</div>
            <div className="text-xs text-[#F5EFE0]/50">Fotos y experiencias de nuestros viajes</div>
          </a>
        </div>
      </section>

      <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Datos</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0] mb-6">Información de contacto</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#D4AF37] mt-0.5" />
                <div>
                  <div className="text-[#F5EFE0] font-medium">+57 312 361 6674</div>
                  <div className="text-xs text-[#F5EFE0]/55">Teléfono y WhatsApp</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#D4AF37] mt-0.5" />
                <div>
                  <div className="text-[#F5EFE0] font-medium">transporteenvanesybuses@gmail.com</div>
                  <div className="text-xs text-[#F5EFE0]/55">Correo principal</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] mt-0.5" />
                <div>
                  <div className="text-[#F5EFE0] font-medium">Bogotá, Colombia</div>
                  <div className="text-xs text-[#F5EFE0]/55">Base de operaciones</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#D4AF37] mt-0.5" />
                <div>
                  <div className="text-[#F5EFE0] font-medium">Lunes a Domingo · 6 a.m. – 10 p.m.</div>
                  <div className="text-xs text-[#F5EFE0]/55">Horario de atención</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="card-lux p-8">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Zona de cobertura</div>
            <h2 className="font-display text-2xl md:text-3xl text-[#F5EFE0] mb-4">Operamos desde Bogotá hacia toda Colombia</h2>
            <p className="text-sm text-[#F5EFE0]/65 leading-relaxed mb-6">
              Traslados urbanos, servicios intermunicipales y viajes turísticos a los 32 departamentos del país. Salidas coordinadas desde la sabana de Bogotá.
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {["Cundinamarca","Boyacá","Santander","Tolima","Huila","Meta","Antioquia","Eje Cafetero"].map((r) => (
                <span key={r} className="px-3 py-1.5 rounded-full border border-[#D4AF37]/25 text-[#F5EFE0]/80 text-center">{r}</span>
              ))}
              <span className="col-span-2 px-3 py-1.5 rounded-full border border-[#D4AF37] text-[#E8C87A] text-center">Y los demás 24 departamentos bajo consulta</span>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
